import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { loadMessages } from './catalogs';
import { en, type Messages } from './en';
import { createI18n, I18nContext } from './i18n-context';
import { directionOf, localeTag, rememberLocale, type Locale } from './locales';

export interface LoadedLocale {
  readonly locale: Locale;
  readonly messages: Messages;
}

interface I18nProviderProps {
  /** The language to start in, already loaded so the page never flashes another. */
  readonly initial?: LoadedLocale | undefined;
  readonly children: ReactNode;
}

/** The interface's language: its words, formats, and direction, and a way to change it. */
export function I18nProvider({
  initial = { locale: 'en', messages: en },
  children,
}: I18nProviderProps) {
  const [current, setCurrent] = useState(initial);
  const [setLocale] = useState(() => {
    // Only the latest choice applies, however the loads finish.
    let latest = initial.locale;
    return (locale: Locale) => {
      latest = locale;
      rememberLocale(locale);
      void loadMessages(locale).then((messages) => {
        if (latest === locale) setCurrent({ locale, messages });
      });
    };
  });

  useEffect(() => {
    const root = document.documentElement;
    root.lang = localeTag(current.locale);
    root.dir = directionOf(current.locale);
    document.title = current.messages.meta.title;
  }, [current]);

  const value = useMemo(
    () => createI18n(current.locale, current.messages, setLocale),
    [current, setLocale],
  );
  return <I18nContext value={value}>{children}</I18nContext>;
}
